// Perfil: el negocio con que se pide (y cambiarlo), con qué se entra (la cuenta: correo y contraseña, Google, Apple o
// Facebook, y sumar otro proveedor; o, con el celular prendido, el número), la guía de uso, instalar la app en el
// celular y salir. En modo vendedor (decisión D60): quién es, para qué cliente pide, lo que quedó sin mandar y salir.
import { HojaInferior, Icono } from '@reparto/ui';
import { type ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { SeccionMensajes } from '../componentes/AvisosPush';
import { Aviso, Boton } from '../componentes/base';
import { TarjetaUso } from '../componentes/EnlaceUso';
import { EntrarCon, IconoCuenta } from '../componentes/EntrarCon';
import { Pantalla } from '../componentes/Marco';
import { salir, useCuenta, useNegocio, vincularProveedor } from '../datos/cuenta';
import { proveedoresDe } from '../datos/entrada';
import { telefonoVisible } from '../datos/formato';
import { NOMBRE_PROVEEDOR, type Proveedor } from '../datos/google';
import { esIPhone, instalar, useInstalar } from '../datos/instalar';
import { useMarca } from '../datos/marca';
import { useSesion, useVendedor } from '../datos/sesion';
import { usePendientes } from '../datos/vendedor';
import type { CuentaTienda, MetodoEntrada } from '../datos/tipos';
import { EnlacePrivacidad } from './Privacidad';
import { Pendientes } from './Visitas';
import type { AvisoVinculo } from './VueltaGoogle';

/** "tu correo y tu contraseña", "Google"… */
const nombreMetodo = (m: MetodoEntrada): string => (m === 'correo' ? 'tu correo y tu contraseña' : NOMBRE_PROVEEDOR[m]);

export function Perfil() {
  const vendedor = useVendedor();
  return vendedor ? <PerfilVendedor /> : <PerfilCliente />;
}

function PerfilVendedor() {
  const perfil = useCuenta((s) => s.perfil)!;
  const vendedor = useVendedor()!;
  const negocio = useNegocio();
  const sinMandar = usePendientes((s) => s.lista.length);
  const navegar = useNavigate();
  const [saliendo, setSaliendo] = useState(false);
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  return (
    <Pantalla titulo="Tu cuenta" subtitulo={perfil.empresa.nombre}>
      <div className="flex flex-col gap-4 px-4 py-4">
        <section aria-labelledby="vendedor" className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-primary-50 text-primary-700">
              <Icono nombre="badge" tamano={30} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] text-stone-600">Modo vendedor</p>
              <h2 id="vendedor" className="text-xl leading-snug font-bold text-stone-900">
                {vendedor.nombre}
              </h2>
              {vendedor.email ? (
                <p className="text-[15px] break-words text-stone-600">
                  <CorreoPartido email={vendedor.email} />
                </p>
              ) : null}
            </div>
          </div>
          <dl className="mt-3 divide-y divide-stone-100 text-[17px]">
            <Fila titulo="Clientes en tu cartera">{perfil.clientes.length}</Fila>
            <Fila titulo="Tus pedidos">
              {perfil.preventa?.aprobacion === 'automatica' ? 'Se aprueban solos' : 'Pasan por Pedidos'}
            </Fila>
            {negocio ? <Fila titulo="Pidiendo para">{negocio.nombre}</Fila> : null}
          </dl>
          <Boton variante="secundario" bloque icono="route" className="mt-3" onClick={() => navegar('/visitas')}>
            Mis visitas de hoy
          </Boton>
        </section>

        <Pendientes />

        <Instalar />

        <Boton variante="peligro" bloque tamano="lg" icono="logout" onClick={() => setConfirmarSalida(true)}>
          Salir
        </Boton>
        <div className="flex flex-col items-center text-center text-[15px] text-stone-600">
          <p>Tienda de {perfil.empresa.nombre}</p>
          <EnlacePrivacidad />
        </div>
      </div>

      <HojaInferior
        abierta={confirmarSalida}
        onCerrar={() => setConfirmarSalida(false)}
        titulo="¿Salir del modo vendedor?"
      >
        <p className="text-[17px] text-stone-700">
          Para volver a entrar usás el correo y la contraseña de tu usuario.
          {sinMandar
            ? ` Tenés ${sinMandar === 1 ? 'una cosa' : `${sinMandar} cosas`} sin mandar: quedan guardadas en este celular y se mandan cuando vuelvas a entrar.`
            : ''}
        </p>
        <div className="mt-5 flex flex-col gap-3">
          <Boton
            variante="peligro"
            bloque
            tamano="lg"
            icono="logout"
            cargando={saliendo}
            onClick={async () => {
              setSaliendo(true);
              await salir();
            }}
          >
            Sí, salir
          </Boton>
          <Boton variante="secundario" bloque tamano="lg" onClick={() => setConfirmarSalida(false)}>
            No, quedarme
          </Boton>
        </div>
      </HojaInferior>
    </Pantalla>
  );
}

function PerfilCliente() {
  const perfil = useCuenta((s) => s.perfil)!;
  const negocio = useNegocio()!;
  const sesion = useSesion((s) => s.sesion);
  const telefono = sesion ? sesion.telefono : perfil.telefono;
  const cuenta: CuentaTienda | null =
    perfil.cuenta ??
    sesion?.cuenta ??
    (perfil.google ? { ...perfil.google, email_verificado: true, metodos: ['google'] } : null);
  const navegar = useNavigate();
  const [saliendo, setSaliendo] = useState(false);
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  const conCodigo = useMarca((s) => s.marca?.acceso.codigo ?? false);
  const acceso = useMarca((s) => s.marca?.acceso);
  // Los proveedores del servidor que la cuenta todavía no tiene: se pueden sumar.
  const paraSumar = proveedoresDe(acceso).filter((p) => !cuenta?.metodos.includes(p));
  // Volvió de vincular un proveedor (VueltaGoogle deja el resultado).
  const { state } = useLocation() as { state: Partial<AvisoVinculo> | null };
  const vinculo = state?.vinculo ?? null;
  const metodos = cuenta?.metodos ?? [];
  return (
    <Pantalla titulo="Tu cuenta" subtitulo={perfil.empresa.nombre}>
      <div className="flex flex-col gap-4 px-4 py-4">
        <section aria-labelledby="negocio" className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-primary-50 text-primary-700">
              <Icono nombre="storefront" tamano={30} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] text-stone-600">Pedís para</p>
              <h2 id="negocio" className="text-xl leading-snug font-bold text-stone-900">
                {negocio.nombre}
              </h2>
            </div>
          </div>
          <dl className="mt-3 divide-y divide-stone-100 text-[17px]">
            {negocio.direccion ? <Fila titulo="Dirección">{negocio.direccion}</Fila> : null}
            {negocio.zona ? <Fila titulo="Zona">{negocio.zona}</Fila> : null}
            {negocio.lista_precio ? <Fila titulo="Lista de precios">{negocio.lista_precio}</Fila> : null}
            {negocio.codigo ? <Fila titulo="Código de cliente">{negocio.codigo}</Fila> : null}
          </dl>
          {perfil.clientes.length > 1 ? (
            <Boton variante="secundario" bloque icono="swap_horiz" className="mt-3" onClick={() => navegar('/negocio')}>
              Cambiar de negocio ({perfil.clientes.length})
            </Boton>
          ) : null}
        </section>

        {negocio.estado_tienda === 'pendiente' ? (
          <Aviso tono="aviso" icono="hourglass_top" titulo="Tu cuenta está en revisión">
            La distribuidora revisa tus datos antes de tu primer pedido. Mientras tanto podés ver los productos.
          </Aviso>
        ) : null}

        {vinculo ? (
          <Aviso tono={vinculo.ok ? 'ok' : 'peligro'} icono={vinculo.ok ? 'check_circle' : 'error'}>
            {vinculo.texto}
          </Aviso>
        ) : null}

        <section aria-labelledby="cuenta" className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm">
          {telefono ? (
            <div className="flex items-center gap-3">
              <Icono nombre="smartphone" tamano={28} className="shrink-0 text-primary-700" />
              <div>
                <p id="cuenta" className="text-[15px] text-stone-600">
                  Entraste con el número
                </p>
                <p className="text-lg font-semibold tabular-nums">{telefonoVisible(telefono)}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <IconoCuenta metodo={metodos[0]} foto={cuenta?.foto_url ?? null} />
              <div className="min-w-0">
                <p id="cuenta" className="text-[15px] text-stone-600">
                  Tu cuenta
                </p>
                <p className="text-lg font-semibold break-words">
                  {cuenta?.email ? <CorreoPartido email={cuenta.email} /> : cuenta?.nombre}
                </p>
              </div>
            </div>
          )}
          {metodos.length ? (
            <p className="mt-3 text-[15px] text-stone-700">
              Entrás con <b>{metodos.map(nombreMetodo).join(', ')}</b>.
            </p>
          ) : null}
          {telefono && negocio.cuentas_google?.length ? (
            <p className="mt-3 text-[15px] text-stone-700">
              También entran a este negocio: <b className="break-all">{negocio.cuentas_google.join(', ')}</b>
            </p>
          ) : null}
          {paraSumar.length ? <SumarProveedor negocio={negocio} proveedores={paraSumar} /> : null}
        </section>

        <SeccionMensajes />

        <TarjetaUso />

        <Instalar />

        <Boton variante="peligro" bloque tamano="lg" icono="logout" onClick={() => setConfirmarSalida(true)}>
          Salir
        </Boton>
        <div className="flex flex-col items-center text-center text-[15px] text-stone-600">
          <p>Tienda de {perfil.empresa.nombre}</p>
          <EnlacePrivacidad />
        </div>
      </div>

      <HojaInferior abierta={confirmarSalida} onCerrar={() => setConfirmarSalida(false)} titulo="¿Salir de la tienda?">
        <p className="text-[17px] text-stone-700">
          {telefono
            ? `Para volver a entrar vas a necesitar ${conCodigo ? 'un código nuevo' : 'tu correo y tu contraseña'}.`
            : `Para volver a entrar usás ${metodos.length ? metodos.map(nombreMetodo).join(' o ') : 'tu cuenta'}.`}{' '}
          Lo que tengas en el pedido queda guardado en este celular.
        </p>
        <div className="mt-5 flex flex-col gap-3">
          <Boton
            variante="peligro"
            bloque
            tamano="lg"
            icono="logout"
            cargando={saliendo}
            onClick={async () => {
              setSaliendo(true);
              await salir();
            }}
          >
            Sí, salir
          </Boton>
          <Boton variante="secundario" bloque tamano="lg" onClick={() => setConfirmarSalida(false)}>
            No, quedarme
          </Boton>
        </div>
      </HojaInferior>
    </Pantalla>
  );
}

/**
 * Sumar Google, Apple o Facebook a la cuenta (decisiones D48 y D51): la próxima vez entra con un toque. Así se suma
 * Facebook a una cuenta que ya tiene ese correo (Facebook no se une solo por el correo: no garantiza que esté
 * verificado). Con la sesión de un teléfono, la cuenta del proveedor queda con acceso al negocio.
 */
function SumarProveedor({ negocio, proveedores }: { negocio: { id: string }; proveedores: Proveedor[] }) {
  return (
    <div className="mt-4 border-t border-stone-100 pt-4">
      <p className="text-[15px] text-stone-700">
        Sumá otra forma de entrar, con un toque y sin escribir la contraseña.
      </p>
      <EntrarCon className="mt-3" proveedores={proveedores} alTocar={(p) => vincularProveedor(p, negocio.id)} />
    </div>
  );
}

function Fila({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-stone-600">{titulo}</dt>
      <dd className="text-right font-medium text-stone-900">{children}</dd>
    </div>
  );
}

function Instalar() {
  const { aviso, instalada } = useInstalar();
  const iphone = esIPhone();
  return (
    <section aria-labelledby="instalar" className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <Icono nombre="install_mobile" tamano={28} className="shrink-0 text-primary-700" />
        <div className="min-w-0 flex-1">
          <h2 id="instalar" className="text-lg font-bold text-stone-900">
            {instalada ? 'Ya tenés la app instalada' : 'Instalar la app'}
          </h2>
          <p className="text-[15px] text-stone-600">
            {instalada
              ? 'La abrís desde el ícono en la pantalla de tu celular, como cualquier otra app.'
              : 'Así la tenés con un ícono en la pantalla del celular y entrás con un toque.'}
          </p>
        </div>
      </div>
      {instalada ? null : aviso ? (
        <Boton bloque tamano="lg" icono="download" className="mt-4" onClick={() => void instalar()}>
          Instalar la app
        </Boton>
      ) : iphone ? (
        <ol className="mt-4 flex flex-col gap-3 text-[17px]">
          <Paso n={1}>
            Tocá <Icono nombre="ios_share" tamano={22} className="text-primary-700" /> <b>Compartir</b>, abajo en
            Safari.
          </Paso>
          <Paso n={2}>
            Elegí <b>Agregar a inicio</b> <Icono nombre="add_box" tamano={22} className="text-primary-700" />.
          </Paso>
          <Paso n={3}>
            Tocá <b>Agregar</b>. Listo: queda el ícono en tu pantalla.
          </Paso>
        </ol>
      ) : (
        <ol className="mt-4 flex flex-col gap-3 text-[17px]">
          <Paso n={1}>
            Tocá el menú del navegador <Icono nombre="more_vert" tamano={22} className="text-primary-700" />, arriba a
            la derecha.
          </Paso>
          <Paso n={2}>
            Elegí <b>Instalar app</b> o <b>Agregar a la pantalla principal</b>.
          </Paso>
        </ol>
      )}
    </section>
  );
}

function Paso({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary-100 text-base font-bold text-primary-800">
        {n}
      </span>
      <span className="pt-0.5 leading-relaxed">{children}</span>
    </li>
  );
}

/** El correo, que si no entra en un renglón se corta después de la arroba (no en cualquier letra). */
function CorreoPartido({ email }: { email: string }) {
  const i = email.indexOf('@');
  if (i < 0) return <>{email}</>;
  return (
    <>
      {email.slice(0, i + 1)}
      <wbr />
      {email.slice(i + 1)}
    </>
  );
}
